import { IconHome2 } from '@tabler/icons-react';
import { FamilyUnit } from '@zorro/clients';
import { useRoles } from '@zorro/web/ui-utils';
import { useEffect } from 'react';

import { useEnrollmentAllowanceStore } from '../../EnrollmentAllowanceCard';
import { HeaderWithIcon } from '../../HeaderWithIcon';
import {
  InsuredFormInputs,
  getInsuredFormIsDirty,
  useEnrollmentInsuredForm,
} from '../../InsuredForm';
import {
  ManualSubmissionStep,
  ManualSubmissionStepSubmit,
  ManualSubmissionStepType,
} from '../ManualSubmissionStep';

const isEmployeeMode = false;
const shouldReloadPage = false;

type Props = {
  employerId: string;
  employeeId: string;
  onboardingPeriodId: string;
  onSubmit: () => void;
  isDon?: boolean;
};

export const ManualSubmissionHouseholdInformationStep = ({
  employerId,
  employeeId,
  onboardingPeriodId,
  onSubmit,
  isDon = false,
}: Props) => {
  const { isZorroOperations } = useRoles();
  const isFinalizationMode = !isZorroOperations;
  const insuredForm = useEnrollmentInsuredForm({
    isFinalizationMode,
    isEmployeeMode,
    onboardingPeriodId,
    employeeId,
    employerId,
    shouldReloadPage,
  });

  const setFamilyUnit = useEnrollmentAllowanceStore(
    (state) => state.setFamilyUnit
  );
  const setNumberOfDependents = useEnrollmentAllowanceStore(
    (state) => state.setNumberOfDependents
  );
  const setIsDirty = useEnrollmentAllowanceStore((state) => state.setIsDirty);

  const isFormDirty = getInsuredFormIsDirty(insuredForm);
  const numberOfDependents =
    insuredForm.dependentsForm.watch('dependents').length;

  useEffect(() => {
    const hasSpouse = insuredForm.spouseStatus.isSpouseActive;
    const hasDependents = insuredForm.hasAnyDependents;

    let familyUnit = FamilyUnit.FAMILY;

    if (!(hasSpouse || hasDependents)) {
      familyUnit = FamilyUnit.EMPLOYEE_ONLY;
    }
    if (!hasSpouse && hasDependents) {
      familyUnit = FamilyUnit.EMPLOYEE_CHILD;
    }
    if (hasSpouse && !hasDependents) {
      familyUnit = FamilyUnit.EMPLOYEE_SPOUSE;
    }

    setFamilyUnit(familyUnit);
  }, [insuredForm.spouseStatus, insuredForm.hasAnyDependents]);

  useEffect(() => {
    if (numberOfDependents === 1 || numberOfDependents === 2) {
      setNumberOfDependents(numberOfDependents);
    } else {
      setNumberOfDependents(null);
    }
  }, [numberOfDependents, setNumberOfDependents]);

  useEffect(() => {
    if (isFormDirty) {
      setIsDirty(true);
    }
  }, [isFormDirty, setIsDirty]);

  const handleStepSubmit: ManualSubmissionStepSubmit = async () => {
    onSubmit();
    return { navigate: await insuredForm.onSubmit() };
  };

  return (
    <ManualSubmissionStep
      step={ManualSubmissionStepType.HOUSEHOLD_INFORMATION}
      submitLabel="Continue"
      onboardingPeriodId={onboardingPeriodId}
      onSubmit={handleStepSubmit}
      employerId={employerId}
      employeeId={employeeId}
      isDon={isDon}
      withReviewCard
    >
      <HeaderWithIcon
        icon={IconHome2}
        title="Household information"
        subtitle="Provide or confirm full details of the employee and other applicants
            for this election"
      />
      <InsuredFormInputs
        isFinalizationMode={isFinalizationMode}
        onboardingPeriodId={onboardingPeriodId}
        isEmployeeMode={isEmployeeMode}
        insuredForm={insuredForm}
        employerId={employerId}
      />
    </ManualSubmissionStep>
  );
};
